/**
 * MigrationCanaryReviewModal — Spec §16.12 / §9.10.
 *
 * Triggered from MigrationJobDetailPanel when status=awaiting_canary_approval.
 *
 * The actual review happens in the user's regular EdgeMail inbox UI: the
 * canary-imported messages live in a `Migration Preview/<job_id>` folder
 * the admin can browse with all the normal mail tools (read, search,
 * preview attachments). This modal is the decision surface only:
 *
 *   - PROMOTE: backend moves the canary messages to their real folders and
 *     resumes the full import (CanaryService.promote, PR 12).
 *   - CANCEL : backend purges the preview messages and sets the job to
 *     'cancelled' (CanaryService.cancel, PR 12).
 *
 * Modify-and-re-canary is just cancel + create a new job with adjusted
 * options — handled by the wizard, not this modal.
 */
import React, { useState } from 'react';
import {
  XMarkIcon,
  RocketLaunchIcon,
  StopIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  EnvelopeIcon,
  FolderOpenIcon,
} from '@heroicons/react/24/outline';
import {
  usePromoteMigrationCanary,
  useCancelMigrationJob,
} from '../../hooks/useMigrationJobs';

interface MigrationCanaryReviewModalProps {
  jobId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onActionComplete?: () => void;
  /** Email address of the target mailbox; surfaced so the admin knows which mailbox to open. */
  targetMailboxEmail?: string;
  /** How many messages live in the preview folder right now (job.imported + job.deduped). */
  previewMessageCount?: number;
}

export function MigrationCanaryReviewModal({
  jobId,
  isOpen,
  onClose,
  onActionComplete,
  targetMailboxEmail,
  previewMessageCount,
}: MigrationCanaryReviewModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const promoteMutation = usePromoteMigrationCanary();
  const cancelMutation = useCancelMigrationJob();

  if (!isOpen || !jobId) return null;

  const previewFolderPath = `Migration Preview/${jobId}`;
  const handleCopyPath = async () => {
    try {
      await navigator.clipboard.writeText(previewFolderPath);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable; user can still read the value */
    }
  };

  const handlePromote = async () => {
    setError(null);
    try {
      await promoteMutation.mutateAsync(jobId);
      onActionComplete?.();
    } catch (e) {
      setError(e && typeof e === 'object' && 'message' in e ? (e as Error).message : 'Promote failed');
    }
  };

  const handleCancel = async () => {
    setError(null);
    try {
      await cancelMutation.mutateAsync(jobId);
      onActionComplete?.();
    } catch (e) {
      setError(e && typeof e === 'object' && 'message' in e ? (e as Error).message : 'Cancel failed');
    }
  };

  const busy = promoteMutation.isPending || cancelMutation.isPending;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-lg shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <RocketLaunchIcon className="h-5 w-5 text-lex-accent" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Review canary import</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm text-gray-700 dark:text-gray-300">
          <p>
            We imported the first <strong>{(previewMessageCount ?? 0).toLocaleString()}</strong> message{(previewMessageCount ?? 0) === 1 ? '' : 's'} into a private review folder so you can spot-check before the full migration runs. Nothing else has been moved yet.
          </p>

          {/* Where to look — the actionable instructions block. */}
          <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <FolderOpenIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-semibold text-blue-900 dark:text-blue-100">Where to find them</div>
                <div className="mt-1 text-blue-800 dark:text-blue-200">
                  Open the EdgeMail inbox{' '}
                  {targetMailboxEmail && (
                    <>
                      for <span className="font-mono inline-flex items-center gap-1"><EnvelopeIcon className="h-3 w-3" />{targetMailboxEmail}</span>
                      {' '}
                    </>
                  )}
                  and look in the folder list for:
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 font-mono text-xs bg-white dark:bg-gray-900 border border-blue-200 dark:border-blue-700 rounded px-2 py-1.5 truncate text-gray-900 dark:text-gray-100">
                    {previewFolderPath}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopyPath}
                    title="Copy folder path"
                    className="inline-flex items-center gap-1 px-2 py-1.5 rounded bg-white dark:bg-gray-900 border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-xs"
                  >
                    {copied ? <><CheckIcon className="h-3.5 w-3.5" />Copied</> : <><ClipboardDocumentIcon className="h-3.5 w-3.5" />Copy</>}
                  </button>
                </div>
                <a
                  href="/email"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  Open EdgeMail in a new tab
                </a>
                <p className="mt-2 text-xs text-blue-700/80 dark:text-blue-300/80">
                  The folder may sort under <em>M</em> in the folder tree; use the search box if you don't see it. It vanishes automatically once you Promote or Cancel.
                </p>
              </div>
            </div>
          </div>

          <div>
            <div className="font-medium text-gray-800 dark:text-gray-200 mb-1">What to check</div>
            <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-400">
              <li>Open a few messages — bodies look right (no garbled characters)</li>
              <li>Open an attachment — it downloads and previews correctly</li>
              <li>Reply chains thread properly</li>
              <li>Read / flagged state matches what you expect from the source</li>
            </ul>
          </div>

          <p className="text-gray-600 dark:text-gray-400">
            When the preview looks right, <strong>Promote</strong> moves these messages to their real folders and resumes the full migration. If anything looks wrong, <strong>Cancel migration</strong> purges them so you can re-run with adjusted options.
          </p>

          {error && (
            <div className="flex items-start gap-2 text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/20 p-3 rounded">
              <ExclamationCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={handleCancel}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium border border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {cancelMutation.isPending
              ? <ArrowPathIcon className="h-4 w-4 animate-spin" />
              : <StopIcon className="h-4 w-4" />
            }
            Cancel migration
          </button>
          <button
            onClick={handlePromote}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded bg-lex-accent text-white text-sm font-medium hover:bg-lex-accent-strong disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {promoteMutation.isPending
              ? <ArrowPathIcon className="h-4 w-4 animate-spin" />
              : <RocketLaunchIcon className="h-4 w-4" />
            }
            Promote
          </button>
        </div>
      </div>
    </div>
  );
}

export default MigrationCanaryReviewModal;
